<script setup>
import { computed } from "vue";
import { usePortalContentAndDesignStore } from "../../../../stores/partners/portalContentAndDesignStore";
import Icon from "../../../../elements/Icon.vue";

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const templates = computed(() => portalContentAndDesignStore.designTemplates || []);
const appliedId = computed(() => portalContentAndDesignStore.appliedDesignTemplateId);

const applyTemplate = (template) => {
    portalContentAndDesignStore.applyDesignTemplate(template);
};

const previewBackgroundStyle = (preview) => {
    if (preview.gradient) {
        return { backgroundImage: `linear-gradient(180deg, ${preview.gradient}, ${preview.background})` };
    }
    return { backgroundColor: preview.background };
};
</script>

<template>
    <div class="space-y-4">
        <p class="text-sm text-gray-500">
            Pick a starting point, then fine-tune it in the other tabs. Applying a theme updates colors,
            fonts, layout and the login/signup sidebar — your logo and uploaded images are kept.
        </p>

        <div class="grid grid-cols-1 gap-3">
            <button
                v-for="template in templates"
                :key="template.id"
                type="button"
                @click="applyTemplate(template)"
                class="group relative text-left rounded-lg border transition-all overflow-hidden focus:outline-none"
                :class="appliedId === template.id
                    ? 'border-teal-500 ring-2 ring-teal-500/30'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'"
            >
                <!-- Applied badge -->
                <span
                    v-if="appliedId === template.id"
                    class="absolute top-2 right-2 z-10 inline-flex items-center gap-0.5 bg-teal-600 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                >
                    <icon name="check" size="12" />
                    Applied
                </span>

                <!-- Mini mockup preview -->
                <div
                    class="h-24 w-full p-3 flex flex-col justify-between"
                    :style="previewBackgroundStyle(template.preview)"
                >
                    <div
                        class="h-3 w-full rounded-sm flex items-center px-1.5 gap-1"
                        :style="{ backgroundColor: template.preview.primary }"
                    >
                        <span class="h-1 w-1 rounded-full bg-white/70"></span>
                        <span class="h-1 w-6 rounded-full bg-white/50"></span>
                    </div>
                    <div
                        class="rounded-md p-2 shadow-sm space-y-1.5"
                        :style="{ backgroundColor: template.preview.surface }"
                    >
                        <div class="h-1.5 w-2/3 rounded-full" :style="{ backgroundColor: template.preview.text, opacity: 0.85 }"></div>
                        <div class="h-1.5 w-1/2 rounded-full" :style="{ backgroundColor: template.preview.text, opacity: 0.35 }"></div>
                        <div class="pt-0.5">
                            <span class="inline-block h-3 w-10 rounded-full" :style="{ backgroundColor: template.preview.primary }"></span>
                        </div>
                    </div>
                </div>

                <!-- Label -->
                <div class="px-3 py-2 border-t border-gray-100 bg-white">
                    <div class="flex items-center gap-1.5">
                        <span class="text-sm font-medium text-gray-900">{{ template.name }}</span>
                        <span
                            v-if="template.has_sidebar"
                            class="inline-block text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded"
                        >
                            Sidebar
                        </span>
                    </div>
                    <p class="text-xs text-gray-500 mt-0.5 leading-snug">{{ template.description }}</p>
                </div>
            </button>
        </div>

        <p v-if="appliedId" class="text-xs text-gray-500">
            Theme applied to the preview. Use <span class="font-medium text-gray-700">Save</span> below to keep it.
        </p>
    </div>
</template>
